import { useState } from "react";

import { Button } from "../../components/Button";
import { ConfirmSheet } from "../../components/ConfirmSheet";
import { AreaField } from "../../components/Fields";
import { I } from "../../components/icons";
import { useInstallPrompt } from "../../components/InstallBar";
import { Notice } from "../../components/Notice";
import { DiscloseRow, NavRow, RowGroup } from "../../components/Rows";
import { useUpdateReady } from "../../components/UpdateBar";
import { dayMonth, todayStr } from "../../domain/date";
import { LINKED_CAN } from "../../domain/family";
import { foodLogCsv, foodLogFileName, foodLogSummary } from "../../domain/foodExport";
import type { KayaState } from "../../domain/types";
import { isIOS, isStandalone } from "../../lib/device";
import { useLinks } from "../../lib/linked";
import { foodByCode, loadFoods } from "../../lib/foodDb";
import { shareOrDownload } from "../../lib/shareFile";
import { applyUpdate, checkNow, reinstall } from "../../lib/swUpdate";
import { defaultState, hydrate } from "../../store/defaults";
import { useKaya } from "../../store/kayaStore";
import { api, type LinkInfo } from "../../sync/api";
import SettingsPage from "./SettingsPage";

function stamp(ms: number): string {
  const d = new Date(ms);
  const day = d.toLocaleDateString(undefined, {
    weekday: "short",
    day: "numeric",
    month: "short",
  });
  const time = d.toLocaleTimeString(undefined, {
    hour: "2-digit",
    minute: "2-digit",
    hour12: false,
  });
  return `${day} ${time}`;
}

/** Where the data lives: the account it syncs to, the copy you can take away, the app itself, and
 *  — at the foot, apart from everything else — the row that throws it all away.
 *  No signed-out branch: nothing renders without an approved account (ADR-020). */
export default function AccountSettings() {
  const { S, replaceAll, showToast, user, logout, syncNow, syncStatus, syncError, online, lastSync } = useKaya();
  const [backup, setBackup] = useState("");
  const [opened, setOpened] = useState(0);
  const { canInstall, install } = useInstallPrompt();
  const updateReady = useUpdateReady();
  const [checked, setChecked] = useState<"current" | "unsupported" | null>(null);
  const [checking, setChecking] = useState(false);
  const [ask, setAsk] = useState<{ kind: "restore"; state: KayaState } | { kind: "erase" } | null>(null);
  const [exporting, setExporting] = useState(false);
  const log = foodLogSummary(S);

  const syncLabel =
    !online || syncStatus === "offline"
      ? "Offline — syncs when you are back"
      : syncStatus === "syncing"
        ? "Syncing…"
        : syncStatus === "error"
          ? "Sync failed"
          : "Up to date";

  function copyBackup() {
    const t = JSON.stringify(S);
    const manual = () => {
      setBackup(t);
      setOpened((n) => n + 1);
      showToast("Copy the text below manually");
    };
    if (navigator.clipboard?.writeText)
      navigator.clipboard
        .writeText(t)
        .then(() => showToast("Backup copied"))
        .catch(manual);
    else manual();
  }

  function restoreBackup() {
    try {
      const o: unknown = JSON.parse(backup);
      const cand = o as Partial<KayaState>;
      if (!cand.profile || !cand.logs) throw new Error("not a backup");
      setAsk({ kind: "restore", state: hydrate(cand) });
    } catch {
      showToast("That isn't a valid backup");
    }
  }

  async function exportFood() {
    setExporting(true);
    try {
      // The table says which rows are estimated dishes. Offline and never loaded, the file is still
      // written — only those rows' source cells are left blank.
      await loadFoods().catch(() => undefined);
      const csv = foodLogCsv(S, (code) => foodByCode(code)?.src);
      // The byte-order mark is what makes Excel read the file as UTF-8 rather than mangle "·" or "é".
      const done = await shareOrDownload(foodLogFileName(todayStr()), "﻿" + csv, "text/csv");
      if (done === "downloaded") showToast("Food log saved");
    } catch {
      showToast("Couldn't export the food log");
    } finally {
      setExporting(false);
    }
  }

  function confirmAsk() {
    if (!ask) return;
    replaceAll(ask.kind === "restore" ? ask.state : defaultState());
    showToast(ask.kind === "restore" ? "Restored" : "Erased");
    setAsk(null);
  }

  async function check() {
    setChecking(true);
    const r = await checkNow();
    setChecking(false);
    if (r === "ready") showToast("A new version is ready");
    else if (r === "behind") {
      // The server has a build this phone never installed: start again from the server's copy.
      showToast("This phone is on an older build than the server. Reloading onto the new one…");
      window.setTimeout(() => void reinstall(), 1500);
    } else {
      setChecked(r);
      showToast(r === "current" ? "You are on the latest version" : "Updates are handled by your browser here");
    }
  }

  const installed = isStandalone();
  const installHow = isIOS()
    ? "In Safari: Share, then Add to Home Screen"
    : "In Chrome: menu ⋮, then Install app. Opened inside another app? Open it in Chrome first.";

  return (
    <SettingsPage title="Account & data" intro="Where your records live, and the copy you can take away.">
      {syncError ? <Notice tone="danger">{syncError}</Notice> : null}

      <RowGroup heading="Sync">
        <NavRow
          kind="action"
          icon={I["cloud-sync"]}
          tone={syncStatus === "error" ? "danger" : "default"}
          title={syncLabel}
          subtitle={lastSync ? `Last sync ${stamp(lastSync)}` : "Not synced yet on this device"}
          action={
            <Button variant="ghost" onClick={() => void syncNow({ force: true })} disabled={!online}>
              Sync now
            </Button>
          }
        />
      </RowGroup>

      <LinkedToYou />

      <RowGroup heading="Your data">
        <NavRow
          kind="action"
          icon={I.dl}
          title="Backup"
          subtitle="Your whole record, copied as text"
          action={
            <Button variant="ghost" onClick={copyBackup}>
              Copy
            </Button>
          }
        />
        {/* Keyed so a failed clipboard can open it again with the text already in the box. */}
        <DiscloseRow
          key={opened}
          icon={I.refresh}
          title="Restore"
          subtitle="Replaces this phone's data"
          defaultOpen={opened > 0}
        >
          <div className="you-disclose-stack">
            <AreaField label="Backup text" value={backup} placeholder="Paste a backup here" onChange={setBackup} />
            <div className="you-actions">
              <Button variant="ghost" onClick={restoreBackup} disabled={!backup.trim()}>
                Restore from text
              </Button>
            </div>
          </div>
        </DiscloseRow>
        <NavRow
          kind="action"
          icon={I.list}
          title="Export the food log"
          subtitle={log.entries ? "A CSV file of every entry, to share or save" : "Nothing logged yet"}
          action={
            <Button
              variant="ghost"
              ariaLabel="Export the food log as CSV"
              busy={exporting}
              disabled={!log.entries}
              onClick={() => void exportFood()}
            >
              Export
            </Button>
          }
        />
        <DiscloseRow icon={I.info} title="What’s in the file">
          <div className="you-disclose-stack">
            <p>
              {log.first
                ? `One row per entry since ${dayMonth(log.first)} — ${log.entries.toLocaleString()} ${
                    log.entries === 1 ? "entry" : "entries"
                  } on ${log.days} ${log.days === 1 ? "day" : "days"}: `
                : "One row per entry: "}
              date, time, meal, food, portion, grams, kcal, protein, carbohydrate, fat, fibre, saturated fat,
              sodium, calcium, iron, potassium, and where the figures came from (the table, an estimated dish,
              or yours).
            </p>
            <p>
              A value Kaya doesn’t know is left blank, never 0. It opens in any spreadsheet — for a doctor, a
              dietitian, or your own files.
            </p>
          </div>
        </DiscloseRow>
      </RowGroup>

      <RowGroup heading="This app">
        {installed ? (
          <NavRow kind="value" icon={I.phone} title="Install app" subtitle="Running as an app" value="Installed" />
        ) : canInstall ? (
          <NavRow
            kind="action"
            icon={I.phone}
            title="Install app"
            subtitle="Full screen, offline, and a home-screen icon"
            action={
              <Button variant="ghost" icon={I.dl} onClick={() => void install()}>
                Install
              </Button>
            }
          />
        ) : (
          <NavRow kind="value" icon={I.phone} title="Install app" subtitle={installHow} />
        )}
        <NavRow
          kind="value"
          icon={I.info}
          title="Version"
          subtitle="Kaya updates itself in the background and offers a refresh when a build is ready"
          value={
            updateReady
              ? "Update ready"
              : checked === "current"
                ? "Latest"
                : checked === "unsupported"
                  ? "Browser-managed"
                  : undefined
          }
        />
        <NavRow
          kind="action"
          icon={I.refresh}
          title={updateReady ? "Use the new version" : "Check for updates"}
          subtitle={updateReady ? "Refresh onto it — your data is untouched" : "Pull a new build now rather than wait"}
          action={
            updateReady ? (
              <Button onClick={() => void applyUpdate()}>Refresh</Button>
            ) : (
              <Button variant="ghost" busy={checking} onClick={() => void check()}>
                Check
              </Button>
            )
          }
        />
      </RowGroup>

      <RowGroup heading="Account">
        <NavRow
          kind="action"
          icon={I.user}
          title={user?.email ?? "–"}
          subtitle="Signed in on this phone"
          action={
            <Button variant="ghost" onClick={logout}>
              Sign out
            </Button>
          }
        />
      </RowGroup>

      <RowGroup>
        <NavRow
          kind="danger"
          icon={I.trash}
          title="Erase all data"
          subtitle="Logs, measurements, badges, settings. This cannot be undone."
          onClick={() => setAsk({ kind: "erase" })}
        />
      </RowGroup>

      <p className="you-fine">
        Calories and steps are estimates (MET × weight × time; steps from speed and height). General fitness guidance,
        not medical advice.
      </p>

      {ask ? (
        <ConfirmSheet
          title={ask.kind === "restore" ? "Replace your data with this backup?" : "Erase all data?"}
          sub={
            ask.kind === "restore"
              ? "Everything on this device is replaced, and the next sync sends it to your account."
              : "Every log, measurement, badge and setting. This cannot be undone."
          }
          confirm={{
            label: ask.kind === "restore" ? "Replace data" : "Erase everything",
            onClick: confirmAsk,
          }}
          onClose={() => setAsk(null)}
        />
      ) : null}
    </SettingsPage>
  );
}

/**
 * Who can read this account through a link (ADR-046): the one place the person being read sees
 * it, with Remove beside each, and a request waiting for their answer. Nothing when there is none.
 */
function LinkedToYou() {
  const { links, reload } = useLinks();
  const showToast = useKaya((s) => s.showToast);
  const [removing, setRemoving] = useState<LinkInfo | null>(null);
  const mine = (links ?? []).filter((l) => l.role === "subject");
  if (!mine.length) return null;

  const nameOf = (l: LinkInfo) => l.person?.name || l.person?.email || "Someone";
  const act = async (fn: () => Promise<unknown>, done: string) => {
    try {
      await fn();
      showToast(done);
      await reload();
    } catch (e) {
      showToast(e instanceof Error ? e.message : "Could not reach Kaya's server");
    }
  };
  const asking = mine.filter((l) => l.status === "pending");
  const linked = mine.filter((l) => l.status === "active");
  const can = LINKED_CAN;

  return (
    <>
      {asking.map((l) => (
        <Notice
          key={l.id}
          tone="info"
          icon={I.user}
          title={`${nameOf(l)} asks to read your plan`}
          primary={{ label: "Allow", onClick: () => void act(() => api.linkAccept(l.id), `${nameOf(l)} is linked`) }}
          secondary={{ label: "Decline", onClick: () => void act(() => api.linkRemove(l.id), "Declined") }}
        >
          {can}. You can remove it here at any time.
        </Notice>
      ))}
      {linked.length ? (
        <RowGroup heading="Linked">
          {linked.map((l) => (
            <NavRow
              key={l.id}
              kind="action"
              icon={I.user}
              title={`${nameOf(l)} can read your plan`}
              subtitle={[
                l.byAdmin ? `Linked by ${nameOf(l)} as the admin` : "You allowed it",
                // what the link sends them is said here too (ADR-056): no notice about you is hidden from you
                l.notifyMissedWeek ? "told when a week of your exercise is missed" : null,
              ]
                .filter(Boolean)
                .join(" · ")}
              action={
                <Button variant="ghost" onClick={() => setRemoving(l)}>
                  Remove
                </Button>
              }
            />
          ))}
        </RowGroup>
      ) : null}
      {linked.length ? <p className="you-fine">{can}. It never logs food or starts a session for you.</p> : null}
      {removing ? (
        <ConfirmSheet
          title={`Stop ${nameOf(removing)} reading your plan?`}
          sub="They can ask again; nothing of yours is deleted."
          confirm={{
            label: "Remove",
            onClick: () => {
              const l = removing;
              setRemoving(null);
              void act(() => api.linkRemove(l.id), "Removed");
            },
          }}
          onClose={() => setRemoving(null)}
        />
      ) : null}
    </>
  );
}
